import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import * as Yup from 'yup';
import { useFormik } from 'formik';

import { routes } from 'utils';
import { _Object } from 'utils/interfaces';
import {
  AsyncSelectField,
  Button,
  LinkButton,
  SelectField,
} from 'views/components/form-inputs';
import {
  productService,
  categoryService,
  brandService,
  supplierService,
} from 'services';

import {
  Breadcrumbs,
  SEOHeader,
  InputField,
  RadioButton,
  TextArea,
} from 'views/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faBox,
  faChevronDown,
  faMagnifyingGlass,
  faCalculator,
  faFileLines,
} from '@fortawesome/free-solid-svg-icons';

const InfoOutlineIcon = ({
  size = 15,
  className = '',
}: {
  size?: number;
  className?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="#212529"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={{ display: 'inline-block', verticalAlign: '-1px' }}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

export const PRODUCT_TYPE_OPTIONS = [
  { label: 'Finished Good', value: 'finished_good' },
  { label: 'Raw Material', value: 'raw_material' },
  { label: 'Composite', value: 'composite' },
];

export const STOCK_UNIT_OPTIONS = [
  { label: 'Kilogram (kg)', value: 'kg' },
  { label: 'Gram (g)', value: 'gram' },
  { label: 'Litre (l)', value: 'litre' },
  { label: 'Millilitre (ml)', value: 'ml' },
  { label: 'Pieces (pcs)', value: 'pcs' },
  { label: 'Bottle (bottle)', value: 'bottle' },
  { label: 'Packet (packet)', value: 'packet' },
  { label: 'Box (box)', value: 'box' },
  { label: 'Bag (bag)', value: 'bag' },
  { label: 'Carton (carton)', value: 'carton' },
  { label: 'Meter (m)', value: 'meter' },
  { label: 'Dozen (dozen)', value: 'dozen' },
];

export const SALE_UNIT_OPTIONS = [
  { label: 'Kilogram (kg)', value: 'kg' },
  { label: 'Gram (g)', value: 'gram' },
  { label: 'Litre (l)', value: 'litre' },
  { label: 'Millilitre (ml)', value: 'ml' },
  { label: 'Pieces (pcs)', value: 'pcs' },
  { label: 'Bottle (bottle)', value: 'bottle' },
  { label: 'Packet (packet)', value: 'packet' },
  { label: 'Box (box)', value: 'box' },
  { label: 'Meter (m)', value: 'meter' },
  { label: 'Dozen (dozen)', value: 'dozen' },
];

export interface IngredientRow {
  id: string;
  product_id?: string;
  ingredient_id?: string;
  name: string;
  sku: string;
  image?: string;
  quantity: number | string;
  unit?: string;
  price?: number;
  cost?: number;
}

const makeAvatar = (name: string) => {
  if (!name) return 'RM';
  const clean = name.replace(/[^a-zA-Z0-9\s]/g, '').trim();
  const parts = clean.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  const first = parts[0][0] || '';
  const second = parts[1][0] || '';
  return `${first}${second}`.toUpperCase();
};

const CreateProduct = () => {
  const { t } = useTranslation();

  const selectAsyncSelect: any = useRef([]);
  const [isEditMode, setEditMode] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<_Object>({
    listing: false,
    edit: false,
    update: false,
  });
  const [categories, setCategories] = useState<_Object>([]);
  const [brands, setBrands] = useState<_Object>([]);
  const [suppliers, setSuppliers] = useState<_Object>([]);
  const [taxRates, setTaxRates] = useState<_Object>([]);
  const [initialData, setInitialData] = useState<_Object>({});

  // Recipe / Ingredients State
  const [useRecipe, setUseRecipe] = useState<boolean>(false);
  const [ingredients, setIngredients] = useState<IngredientRow[]>([]);
  const [productOptions, setProductOptions] = useState<any[]>([]);
  const [activeSearchIndex, setActiveSearchIndex] = useState<number | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState<string>('');
  const searchContainerRefs = useRef<{ [key: number]: HTMLDivElement | null }>(
    {},
  );

  // Close search dropdown on click outside
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (activeSearchIndex !== null) {
        const container = searchContainerRefs.current[activeSearchIndex];
        if (container && !container.contains(e.target as Node)) {
          setActiveSearchIndex(null);
          setSearchQuery('');
        }
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [activeSearchIndex]);

  // Fetch real products from backend for recipe raw material selection
  // Fetch only raw materials from backend for recipe raw material selection
  useEffect(() => {
    productService
      .getProducts({ per_page: 9999, filter: { product_type: 'raw_material' } })
      .then((res: _Object) => {
        const items = res?.items || [];
        const rawMaterials = items.filter(
          (p: _Object) => p.product_type === 'raw_material',
        );
        const opts = rawMaterials.map((p: _Object) => ({
          value: p.id,
          id: p.id,
          name: p.name,
          sku: p.sku || `SKU-${p.id}`,
          price: Number(p.buying_price) || Number(p.regular_price) || 0,
          stock_unit: p.stock_unit || '',
          sale_unit: p.sale_unit || '',
          image: p.featured_image || p.image || null,
        }));
        setProductOptions(opts);
      })
      .catch(() => {
        setProductOptions([]);
      });
  }, []);

  // ONLY real raw material products from backend
  const availableOptions = productOptions;

  const getFilteredOptions = () => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return availableOptions;
    return availableOptions.filter(
      (opt) =>
        opt.name.toLowerCase().includes(q) ||
        (opt.sku && opt.sku.toLowerCase().includes(q)),
    );
  };

  const handleAddIngredient = () => {
    if (!useRecipe) {
      setUseRecipe(true);
    }
    const newRow: IngredientRow = {
      id: `row-${Date.now()}`,
      product_id: '',
      ingredient_id: '',
      name: '',
      sku: '',
      quantity: 1,
      unit: '',
      price: 0,
    };
    setIngredients((prev) => [...prev, newRow]);
    const newIndex = ingredients.length;
    setActiveSearchIndex(newIndex);
    setSearchQuery('');
  };

  const handleRemoveIngredient = (index: number) => {
    setIngredients((prev) => prev.filter((_, i) => i !== index));
    if (activeSearchIndex === index) {
      setActiveSearchIndex(null);
      setSearchQuery('');
    }
  };

  const handleStartSearch = (index: number) => {
    setActiveSearchIndex(index);
    setSearchQuery('');
  };

  const handleSelectProduct = (index: number, selectedOpt: any) => {
    setIngredients((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        product_id: selectedOpt.value || selectedOpt.id,
        ingredient_id: selectedOpt.value || selectedOpt.id,
        name: selectedOpt.name,
        sku: selectedOpt.sku,
        image: selectedOpt.image,
        unit: selectedOpt.sale_unit || selectedOpt.stock_unit || '',
        price: selectedOpt.price || 0,
      };
      return next;
    });
    setActiveSearchIndex(null);
    setSearchQuery('');
  };

  const handleQuantityChange = (index: number, val: string) => {
    setIngredients((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        quantity: val === '' ? '' : Number(val),
      };
      return next;
    });
  };

  const totalCost = ingredients.reduce((sum, item) => {
    const q = Number(item.quantity) || 0;
    const p = Number(item.price) || 0;
    return sum + q * p;
  }, 0);

  const displayedCost = totalCost.toFixed(2);

  useEffect(() => {
    categoryService.getCategories('').then((data: _Object) => {
      setCategories(data.items);
    });
    // tagService.getTags('').then((data: _Object) => {
    //   setTags(data.items)
    // })
    brandService.getBrands('').then((data: _Object) => {
      setBrands(data.items);
    });
    productService.taxRate({ per_page: 9999 }).then((data: _Object) => {
      setTaxRates(data?.items);
    });
  }, []);

  useEffect(() => {
    setInitialData({
      sku: null,
      name: '',
      user_id: null,
      barcode: null,
      brand_id: null,
      category_id: null,
      supplier_id: null,
      description: '',
      regular_price: null,
      buying_price: null,
      manage_stock: false,
      discounted_price: null,
      short_description: '',
      stock_status: 'instock',
      low_stock_threshold: '',
      tax_status: false,
      status: 'published',
      tax_rate_id: null,
      width: null,
      length: null,
      height: null,
      weight: null,
      product_structure: 'standard',
      product_type: 'finished_good',
      is_sale_enabled: true,
      purchase_unit: '',
      stock_unit: '',
      purchase_conversion_factor: 1,
      sale_unit: '',
    });

    setEditMode(false);
    if (id) {
      setLoading({ edit: true });
      setEditMode(true);
      productService.getProductDetails(id).then((data: _Object) => {
        setInitialData({
          ...data,
          categories: data.category
            ? [{ name: data.category?.name, id: data.category?.id }]
            : [],
          suppliers: data.supplier
            ? { label: data.supplier?.name, value: data.supplier?.id }
            : null,
          brand: data.brand
            ? { name: data.brand?.name, id: data.brand?.id }
            : null,
          stock_status:
            data?.stock_status?.length > 0
              ? data.stock_status
              : data.manage_stock === 'true'
                ? 'outofstock'
                : 'instock',
          product_structure: data.product_structure || 'standard',
          product_type:
            data.product_type === 'recipe_based'
              ? 'composite'
              : data.product_type || 'finished_good',
          is_sale_enabled:
            data.is_sale_enabled !== undefined && data.is_sale_enabled !== null
              ? Boolean(data.is_sale_enabled)
              : true,
          purchase_unit: data.purchase_unit || '',
          stock_unit: data.stock_unit || '',
          purchase_conversion_factor: data.purchase_conversion_factor || 1,
          sale_unit: data.sale_unit || '',
        });

        if (data.product_type === 'composite' || data.product_type === 'recipe_based') {
          setUseRecipe(true);
        } else {
          setUseRecipe(false);
        }

        if (Array.isArray(data.ingredients) && data.ingredients.length > 0) {
          const loaded: IngredientRow[] = data.ingredients.map(
            (ing: _Object, idx: number) => ({
              id: ing.id || `row-${idx}`,
              product_id: ing.ingredient_id,
              ingredient_id: ing.ingredient_id,
              name: ing.ingredient?.name || ing.name || 'Raw Material',
              sku: ing.ingredient?.sku || ing.sku || '',
              image: ing.ingredient?.image || null,
              quantity: ing.quantity,
              unit:
                ing.unit ||
                ing.ingredient?.stock_unit ||
                ing.ingredient?.sale_unit ||
                '',
              price: Number(
                ing.cost !== undefined
                  ? ing.cost
                  : ing.ingredient?.buying_price || 0,
              ),
            }),
          );
          setIngredients(loaded);
        } else {
          setIngredients([]);
        }

        setLoading({ edit: false });
      });
    }
  }, [id]);

  const formik: _Object = useFormik({
    initialValues: initialData,
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().label('Title').required(t('product.title_required')),
      // sku: Yup.string().label('SKU').required(t('product.sku_required')),
      // barcode: Yup.string().label('Barcode').required(t('product.barcode_required')),
      // brand_id: Yup.string().label('Brand').required(),
      // category_id: Yup.string().label('Category').required(),
      tax_rate_id:
        initialData.tax_status === false
          ? Yup.string().nullable()
          : Yup.string()
            .label('Tax rate')
            .required(t('product.tax_rate_id_required')),

      regular_price: Yup.number()
        .label('Regular price')
        .min(0, t('product.regular_price_greater_then_zero'))
        .test(
          'decimal-places',
          t('product.regular_price_two_decimal'),
          (value) => {
            if (!value) return true;
            const decimalPlaces = (value.toString().split('.')[1] || []).length;
            return decimalPlaces <= 2;
          },
        )
        .required(t('product.regular_price_required')),

      buying_price: Yup.number()
        .nullable()
        .min(0, t('product.buying_price_greater_then_zero'))
        .test(
          'decimal-places',
          t('product.buying_price_two_decimal'),
          (value) => {
            if (!value) return true;
            const decimalPlaces = (value.toString().split('.')[1] || []).length;
            return decimalPlaces <= 2;
          },
        ),

      discounted_price: Yup.string()
        .nullable()
        .test(
          'decimal-places',
          t('product.discounted_price_two_decimal'),
          (value) => {
            if (!value) return true;
            const decimalPlaces = (value.toString().split('.')[1] || []).length;
            return decimalPlaces <= 2;
          },
        )
        .when('regular_price', ([regular_price], schema) => {
          return schema.test(
            'is-less-than-regular-price',
            t('product.discounted_price_lass_then_regular'),
            function (value: any) {
              const regularPrice = parseFloat(regular_price);
              const discountedPrice = parseFloat(value);

              if (
                !isNaN(regularPrice) &&
                !isNaN(discountedPrice) &&
                discountedPrice >= regularPrice
              ) {
                return this.createError({
                  path: 'discounted_price',
                  message: t('product.discounted_price_lass_then_regular'),
                });
              }
              return true;
            },
          );
        }),

      length: Yup.number()
        .nullable()
        .positive(t('product.length_required'))
        .notOneOf([0], t('product.length_zero_required')),

      width: Yup.number()
        .nullable()
        .positive(t('product.width_required'))
        .notOneOf([0], t('product.width_zero_required')),

      height: Yup.number()
        .nullable()
        .positive(t('product.height_required'))
        .notOneOf([0], t('product.height_zero_required')),

      weight: Yup.number()
        .nullable()
        .positive(t('product.weight_required'))
        .notOneOf([0], t('product.weight_zero_required')),
    }),

    onSubmit: (values) => {
      setLoading({ update: true });
      const computedProductType =
        values.product_type || 'finished_good';

      if (id) {
        const params: _Object = {
          name: values.name,
          sku: values.sku,
          barcode: values?.barcode?.length > 0 ? values.barcode : null,
          description: values.description,
          short_description: values.short_description,
          buying_price: values.buying_price,
          regular_price: values.regular_price,
          discounted_price: values.discounted_price,
          status: values.status,
          manage_stock: values.manage_stock,
          stock_status: values.stock_status,
          low_stock_threshold:
            values.low_stock_threshold > 0 ? values.low_stock_threshold : 0,
          product_structure: values.product_structure || 'standard',
          product_type: computedProductType,
          is_sale_enabled:
            values.is_sale_enabled !== undefined
              ? Boolean(values.is_sale_enabled)
              : true,
          purchase_unit: values.purchase_unit || null,
          stock_unit: values.stock_unit || null,
          purchase_conversion_factor: values.purchase_conversion_factor
            ? Number(values.purchase_conversion_factor)
            : 1,
          sale_unit: values.sale_unit || null,
          weight: values.weight,
          length: values.length,
          width: values.width,
          height: values.height,
          brand_id: values.brand_id,
          category_id: values.category_id,
          supplier_id: values.supplier_id,
          tax_status: values.tax_status,
          tax_rate_id: values.tax_status ? values.tax_rate_id : null,
        };

        if (computedProductType === 'composite' || computedProductType === 'recipe_based') {
          params.ingredients = ingredients
            .filter(
              (item) =>
                (item.ingredient_id || item.product_id) &&
                Number(item.quantity) > 0,
            )
            .map((item) => ({
              ingredient_id: item.ingredient_id || item.product_id,
              quantity: Number(item.quantity),
              unit: item.unit || 'unit',
              cost: Number(item.price || item.cost || 0),
            }));
        }

        // NOTE: sale_conversion_factor is deliberately omitted - backend auto-calculates it!

        productService
          .updateProduct(id, params)
          .then((data: _Object) => {
            setLoading({ update: true, listing: true });
            if (data?.error == false) {
              setLoading({ update: false, listing: false });
              navigate(`${routes.products}`);
            } else {
              setLoading({ update: false, listing: false });
              throw new Error(data?.error);
            }
          })
          .catch((error) => {
            error && setLoading({ update: false, listing: false });
          });
      } else {
        const createPayload: _Object = { ...values };
        delete createPayload.user_id;
        delete createPayload.data;
        delete createPayload.categories;
        delete createPayload.brand;
        delete createPayload.suppliers;
        delete createPayload.sale_conversion_factor; // Backend auto-calculates it!

        createPayload.product_type = computedProductType;
        createPayload.is_sale_enabled =
          values.is_sale_enabled !== undefined
            ? Boolean(values.is_sale_enabled)
            : true;
        createPayload.product_structure =
          values.product_structure || 'standard';
        createPayload.purchase_unit = values.purchase_unit || null;
        createPayload.stock_unit = values.stock_unit || null;
        createPayload.purchase_conversion_factor =
          values.purchase_conversion_factor
            ? Number(values.purchase_conversion_factor)
            : 1;
        createPayload.sale_unit = values.sale_unit || null;
        createPayload.low_stock_threshold =
          values.low_stock_threshold > 0 ? values.low_stock_threshold : 0;
        createPayload.barcode =
          values?.barcode?.length > 0 ? values?.barcode : null;
        createPayload.tax_rate_id = values.tax_status
          ? values.tax_rate_id
          : null;

        if (computedProductType === 'composite' || computedProductType === 'recipe_based') {
          createPayload.ingredients = ingredients
            .filter(
              (item) =>
                (item.ingredient_id || item.product_id) &&
                Number(item.quantity) > 0,
            )
            .map((item) => ({
              ingredient_id: item.ingredient_id || item.product_id,
              quantity: Number(item.quantity),
              unit: item.unit || 'unit',
              cost: Number(item.price || item.cost || 0),
            }));
        }

        productService
          .createProduct(createPayload)
          .then((data: _Object) => {
            setLoading({ update: true, listing: true });
            if (data?.error === false) {
              navigate(`${routes.products}`);
            } else {
              setLoading({ update: false, listing: false });
              throw new Error(data?.error);
            }
          })
          .catch((error) => {
            error && setLoading({ update: false, listing: false });
          });
      }
    },
  });

  const stockUnitOptions = useMemo(() => {
    if (
      formik.values.stock_unit &&
      !STOCK_UNIT_OPTIONS.some((o) => o.value === formik.values.stock_unit)
    ) {
      return [
        { label: formik.values.stock_unit, value: formik.values.stock_unit },
        ...STOCK_UNIT_OPTIONS,
      ];
    }
    return STOCK_UNIT_OPTIONS;
  }, [formik.values.stock_unit]);

  const saleUnitOptions = useMemo(() => {
    if (
      formik.values.sale_unit &&
      !SALE_UNIT_OPTIONS.some((o) => o.value === formik.values.sale_unit)
    ) {
      return [
        { label: formik.values.sale_unit, value: formik.values.sale_unit },
        ...SALE_UNIT_OPTIONS,
      ];
    }
    return SALE_UNIT_OPTIONS;
  }, [formik.values.sale_unit]);

  useEffect(() => {
    supplierService.getSuppliers({}).then((data: _Object) => {
      setSuppliers(data.items);
    });
  }, [formik.values.brand_id]);

  const categoriesOption = async (categoriesValue: string) => {
    const data: _Object = await categoryService.getCategories({
      q: categoriesValue,
    });
    return data.items?.map((item: _Object) => {
      return { label: item.name, value: item.id };
    });
  };

  const brandOption = async (brandValue: string) => {
    const data: _Object = await brandService.getBrands({ q: brandValue });
    return data.items?.map((item: _Object) => {
      return { label: item.name, value: item.id };
    });
  };

  const supplierOption = async (supplierValue: string) => {
    const data: _Object = await supplierService.getSuppliers({
      q: supplierValue,
    });
    return data.items?.map((item: _Object) => {
      return { label: item.name, value: item.id };
    });
  };

  const genrateSku = async () => {
    const data: _Object = await productService.createProductSKU({
      category_id: formik.values.category_ids[0],
      product_title: formik.values.name,
    });

    if (data.error === false) {
      formik.setFieldValue('sku', data.data);
    }
  };

  const genrateBarcode = async () => {
    const data: _Object = await productService.createProductBarcode({
      category_id: formik.values.category_ids[0],
      product_title: formik.values.name,
    });

    if (data.error === false) {
      formik.setFieldValue('barcode', data.data);
    }
  };
  console.log('formik.values.manage_stock', typeof formik.values.manage_stock);

  return (
    <>
      <SEOHeader title="Add product" />

      <Breadcrumbs
        trails={[
          {
            label: t('product.products'),
            path: `${routes.products}`,
          },
          {
            label: `${isEditMode ? t('product.update') : t('product.new')}`,
            path: '',
          },
        ]}
        pageTitle={`${isEditMode ? t('product.update') : t('product.new')}`}
      />
      <form
        className={`row filters gap-3 gap-sm-0 ${loading.edit === true ? 'is-loading' : ''}`}
        onSubmit={formik.handleSubmit}
      >
        <div className="col-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6 mb-xs-3 mb-md-0">
          <div className="card">
            <div className="card-header p-3">
              <h4 className="mb-0">{t('product.general_information')}</h4>
            </div>

            <div className="card-body">
              <div className="row">
                <InputField
                  name="name"
                  label={t('common.title')}
                  required={true}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && formik.errors.name}
                />

                <InputField
                  className="col-4"
                  type="number"
                  name="regular_price"
                  label={t('product.regular_price')}
                  required={true}
                  value={formik.values.regular_price}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.regular_price && formik.errors.regular_price
                  }
                />

                <InputField
                  className="col-4 px-0 px-sm-2"
                  type="number"
                  name="discounted_price"
                  label={t('product.sale_price')}
                  value={formik.values.discounted_price}
                  onChange={(e: _Object) => {
                    formik.setFieldValue(
                      'discounted_price',
                      e?.target?.value || null,
                    );
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.discounted_price &&
                    formik.errors.discounted_price
                  }
                />

                <InputField
                  className="col-4"
                  type="number"
                  name="buying_price"
                  label={t('product.buying_price')}
                  value={formik.values.buying_price}
                  onChange={(e: _Object) => {
                    formik.setFieldValue(
                      'buying_price',
                      e?.target?.value || null,
                    );
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.buying_price && formik.errors.buying_price
                  }
                />

                <SelectField
                  isClearable
                  label={t('common.status')}
                  required={true}
                  className="col-6 col-sm-4"
                  name="status"
                  options={[
                    {
                      label: 'Published',
                      value: 'published',
                    },
                    {
                      label: 'Draft',
                      value: 'draft',
                    },
                    {
                      label: 'Discontinued',
                      value: 'discontinued',
                    },
                  ]}
                  value={{ value: formik?.values?.status }}
                  onChange={(e: _Object) => {
                    formik.setFieldValue('status', e?.value || null);
                  }}
                  getOptionLabel={(option: { label: string }) => option?.label}
                  getOptionValue={(option: { value: string }) => option?.value}
                  error={formik.touched.status && formik.errors.status}
                />

                <RadioButton
                  name="tax_status"
                  required={true}
                  className="col-6 col-sm-4"
                  label={t('product.tax_status')}
                  value={formik?.values?.tax_status}
                  options={[
                    { label: t('common.yes'), value: true },
                    { label: t('common.no'), value: false },
                  ]}
                  onChange={(e: _Object) => {
                    formik.setFieldValue(
                      'tax_status',
                      e?.target?.value === 'true' ? true : false,
                    );
                    setInitialData(() => ({
                      ...formik.values,
                      tax_status: e?.target?.value === 'true' ? true : false,
                      tax_rate_id: e?.target?.value === 'false' ? null : null,
                    }));
                  }}
                />

                {formik?.values?.tax_status === true && (
                  <SelectField
                    isClearable
                    label={t('product.tax_rate')}
                    required={true}
                    className="col-4"
                    name="tax_rate_id"
                    options={taxRates?.map((item: any) => ({
                      label: (
                        <div>
                          <span>{item?.title?.toUpperCase()}</span>{' '}
                          {item?.title ? '-' : ''} <small>{item.rate}%</small>
                        </div>
                      ),
                      value: item?.id,
                    }))}
                    value={{ value: formik?.values?.tax_rate_id }}
                    onChange={(e: _Object) => {
                      formik.setFieldValue('tax_rate_id', e?.value || null);
                    }}
                    getOptionLabel={(option: _Object) => option?.label}
                    getOptionValue={(option: _Object) => option?.value}
                    error={
                      formik.touched.tax_rate_id && formik.errors.tax_rate_id
                    }
                  />
                )}

                <RadioButton
                  name="product_type"
                  label="Product Type"
                  value={formik.values.product_type}
                  options={PRODUCT_TYPE_OPTIONS}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newType = e.target.value;
                    formik.setFieldValue('product_type', newType);
                    setUseRecipe(newType === 'composite' || newType === 'recipe_based');
                  }}
                />

                <RadioButton
                  name="is_sale_enabled"
                  label="Available for Sale (POS & Billing)"
                  value={formik.values.is_sale_enabled}
                  options={[
                    { label: t('common.yes'), value: true },
                    { label: t('common.no'), value: false },
                  ]}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    formik.setFieldValue(
                      'is_sale_enabled',
                      e.target.value === 'true' ? true : false,
                    );
                  }}
                />

                <TextArea
                  name="description"
                  label={t('common.description')}
                  placeholder={t('common.enter_description')}
                  rows={5}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </div>
          </div>

          <div className="col-12 d-none d-md-flex gap-3 my-3">
            <Button
              type="submit"
              name={isEditMode ? t('common.update') : t('common.submit')}
              className="primary"
              loading={loading.update}
            />
            <LinkButton
              path={routes.products}
              label={t('common.cancel')}
              className="link text-danger fw-medium text-decoration-underline"
            />
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6">
          <div className="card recipe-ingredients mb-4">
            <div className="card-header p-3 d-flex align-items-center justify-content-between">
              <h4 className="mb-0">{t('product.manage_stocks')}</h4>

              <LinkButton
                path={`${routes.inventory}?product_id=${id}`}
                label="View Stock Ledger"
                className="primary btn-add-ingredient"
              >
                <FontAwesomeIcon icon={faFileLines} className="me-0" />
              </LinkButton>
            </div>

            <div className="card-body">
              {/* {id && (
                <div className="alert alert-light d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <span className="text-muted me-2">
                      Current Total Stock:
                    </span>
                    <span className="badge bg-primary fs-6 me-2">
                      {initialData.stock || 0} {initialData.stock_unit || ''}
                    </span>
                    <span
                      className={`badge ${initialData.stock_status === 'instock' ? 'bg-success' : 'bg-danger'}`}
                    >
                      {initialData.stock_status === 'instock'
                        ? 'In stock'
                        : 'Out of stock'}
                    </span>
                  </div>

                  <LinkButton
                    path={`${routes.inventory}?product_id=${id}`}
                    label="View Stock Ledger"
                    className="primary btn-add-ingredient"
                  >
                    <FontAwesomeIcon icon={faFileLines} className="me-0" />
                  </LinkButton>
                </div>
              )} */}

              <div className="row">
                <div className="col-6">
                  <InputField
                    name="sku"
                    // required={true}
                    label={t('common.sku')}
                    // linkLabel={t('product.genrate')}
                    linkDisable={
                      formik.values?.name?.length > 0 &&
                      formik.values?.category_ids?.length > 0
                    }
                    linkOnChange={genrateSku}
                    value={formik.values.sku}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.sku && formik.errors.sku}
                  />
                </div>

                <div className="col-6">
                  <InputField
                    type="text"
                    name="barcode"
                    // required={true}
                    label={t('product.barcode')}
                    // linkLabel={t('product.genrate')}
                    linkDisable={
                      formik.values?.name?.length > 0 &&
                      formik.values?.category_ids?.length > 0
                    }
                    linkOnChange={genrateBarcode}
                    value={formik.values.barcode}
                    onChange={formik.handleChange}
                    error={formik.touched.barcode && formik.errors.barcode}
                  />
                </div>

                <InputField
                  className="col-3"
                  label={t('product.dimensions')}
                  type="number"
                  name="weight"
                  placeholder={t('product.weight')}
                  value={formik.values.weight}
                  onBlur={formik.handleBlur}
                  onChange={(e: _Object) => {
                    formik.setFieldValue('weight', e?.target?.value || null);
                  }}
                  error={formik.touched.weight && formik.errors.weight}
                />

                <InputField
                  className="col-3"
                  label="&nbsp;"
                  type="number"
                  name="length"
                  placeholder={t('product.length')}
                  value={formik.values.length}
                  onBlur={formik.handleBlur}
                  onChange={(e: _Object) => {
                    formik.setFieldValue('length', e?.target?.value || null);
                  }}
                  error={formik.touched.length && formik.errors.length}
                />

                <InputField
                  className="col-3"
                  label="&nbsp;"
                  type="number"
                  name="width"
                  placeholder={t('product.width')}
                  value={formik.values.width}
                  onBlur={formik.handleBlur}
                  onChange={(e: _Object) => {
                    formik.setFieldValue('width', e?.target?.value || null);
                  }}
                  error={formik.touched.width && formik.errors.width}
                />

                <InputField
                  className="col-3"
                  type="number"
                  label="&nbsp;"
                  name="height"
                  placeholder={t('product.height')}
                  value={formik.values.height}
                  onBlur={formik.handleBlur}
                  onChange={(e: _Object) => {
                    formik.setFieldValue('height', e?.target?.value || null);
                  }}
                  error={formik.touched.height && formik.errors.height}
                />

                <div className="col-12 col-md-6 col-lg-6 col-xl-3">
                  <AsyncSelectField
                    isClearable
                    cacheOptions
                    // required={true}
                    label={t('category.categories')}
                    name="category_ids"
                    placeholder={t('common.choose_option')}
                    loadOptions={categoriesOption}
                    ref={(ref: any) => (selectAsyncSelect.current = ref)}
                    defaultOptions={categories?.map((item: _Object) => {
                      return {
                        label: item.name,
                        value: item.id,
                      };
                    })}
                    onChange={(val: _Object) => {
                      formik.setFieldValue('category_id', val?.value);
                      formik.setFieldValue('categories', [
                        { name: val?.label, id: val?.value },
                      ]);
                    }}
                    value={
                      formik?.values?.categories &&
                        formik?.values?.categories[0]?.name
                        ? {
                          label: formik.values?.categories[0]?.name,
                          value: formik.values?.categories[0]?.id,
                        }
                        : null
                    }
                    onBlur={formik.handleBlur}
                  // error={!formik.values.category_id && formik.errors.category_id}
                  />
                </div>

                <div className="col-4 col-md-6 col-lg-6 col-xl-3 manage-stock-status">
                  {/* <div className="manage-stock-status d-flex gap-3"> */}
                  <RadioButton
                    name="manage_stock"
                    // className="col-3"
                    label={t('product.manage_stock')}
                    value={formik.values.manage_stock}
                    options={[
                      { label: t('common.yes'), value: true },
                      { label: t('common.no'), value: false },
                    ]}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      formik.setFieldValue(
                        'manage_stock',
                        e.target.value === 'true' ? true : false,
                      );
                    }}
                  />
                </div>

                {formik.values.manage_stock === false && (
                  <div className="col-8 col-md-6 col-lg-6 col-xl-6 manage-stock-status">
                    <RadioButton
                      name="stock_status"
                      // className="col-4"
                      label={t('product.stock_status')}
                      onChange={formik.handleChange}
                      value={formik.values.stock_status}
                      options={[
                        { label: t('product.in_stock'), value: 'instock' },
                        {
                          label: t('product.out_of_stock'),
                          value: 'outofstock',
                        },
                      ]}
                    />
                  </div>
                )}

                {formik.values.manage_stock === true && (
                  <InputField
                    type="number"
                    name="low_stock_threshold"
                    className="col"
                    label={t('product.low_stock_threshold')}
                    value={formik.values.low_stock_threshold}
                    onChange={formik.handleChange}
                  />
                )}

                {formik.values.manage_stock === true && (
                  <div className="col-12">
                    <div className="row">
                      {/* Row 1: Stock Unit & Sale Unit */}
                      <div className="col-12 col-md-6">
                        <SelectField
                          isClearable
                          label="Stock Unit"
                          name="stock_unit"
                          placeholder="Choose Stock Unit (e.g. kg, litre, pcs)"
                          options={stockUnitOptions}
                          value={{ value: formik.values.stock_unit }}
                          onChange={(opt: any) => {
                            formik.setFieldValue(
                              'stock_unit',
                              opt?.value || '',
                            );
                          }}
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <SelectField
                          isClearable
                          label="Sale Unit"
                          name="sale_unit"
                          placeholder="Choose Sale Unit"
                          options={saleUnitOptions}
                          value={{ value: formik.values.sale_unit }}
                          onChange={(opt: any) => {
                            formik.setFieldValue('sale_unit', opt?.value || '');
                          }}
                        />
                      </div>

                      {/* Row 2: Purchase Unit & Purchase Conversion Factor */}
                      <div className="col-12 col-md-6">
                        <InputField
                          label="Purchase Unit"
                          name="purchase_unit"
                          placeholder="e.g. bag, carton, box, kg"
                          value={formik.values.purchase_unit || ''}
                          onChange={(e: any) => {
                            const unitVal = e?.target?.value || '';
                            formik.setFieldValue('purchase_unit', unitVal);
                            const matchNum = unitVal.match(/(\d+)/);
                            if (
                              matchNum &&
                              Number(matchNum[1]) > 0 &&
                              (!formik.values.purchase_conversion_factor ||
                                formik.values.purchase_conversion_factor === 1)
                            ) {
                              formik.setFieldValue(
                                'purchase_conversion_factor',
                                Number(matchNum[1]),
                              );
                            }
                          }}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.purchase_unit &&
                            formik.errors.purchase_unit
                          }
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <InputField
                          type="number"
                          step="any"
                          min="0"
                          label="Purchase Conversion Factor"
                          name="purchase_conversion_factor"
                          placeholder="1"
                          value={formik.values.purchase_conversion_factor}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.purchase_conversion_factor &&
                            formik.errors.purchase_conversion_factor
                          }
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="col-6">
                  <AsyncSelectField
                    isClearable
                    cacheOptions
                    label={t('common.brands')}
                    name="brand_id"
                    // required={true}
                    loadOptions={brandOption}
                    placeholder={t('common.choose_option')}
                    ref={(ref: any) => (selectAsyncSelect.current = ref)}
                    defaultOptions={brands?.map((item: _Object) => ({
                      label: item.name,
                      value: item.id,
                    }))}
                    onChange={(val: _Object) => {
                      formik.setFieldValue('brand_id', val?.value || null);
                      formik.setFieldValue('brand', {
                        name: val?.label,
                        id: val?.value,
                      });
                      if (isEditMode) {
                        formik.setFieldValue('supplier_ids', []);
                        formik.setFieldValue('suppliers', []);
                      }
                    }}
                    value={
                      formik?.values?.brand?.name
                        ? {
                          label: formik.values.brand?.name,
                          value: formik.values.brand?.id,
                        }
                        : null
                    }
                    onBlur={formik.handleBlur}
                  // error={formik.touched.brand_id && formik.errors.brand_id}
                  />
                </div>

                <div className="col-6">
                  <AsyncSelectField
                    isClearable
                    cacheOptions
                    label={t('supplier.suppliers')}
                    name="supplier_ids"
                    disabled={!formik.values.brand_id}
                    loadOptions={supplierOption}
                    placeholder={t('common.choose_option')}
                    ref={(ref: any) => (selectAsyncSelect.current = ref)}
                    defaultOptions={
                      suppliers.map((matchedItem: any) => ({
                        label: matchedItem?.name,
                        value: matchedItem?.id,
                      })) || []
                    }
                    // defaultOptions={suppliers
                    //   .filter((item: any) => item?.brands && item?.brands.some((brand: any) => brand?.id === formik.values.brand_id))
                    //   .map((matchedItem: any) => ({
                    //     label: matchedItem?.name,
                    //     value: matchedItem?.id
                    //   })) || []}

                    onChange={(val: _Object) => {
                      formik.setFieldValue('supplier_id', val.value || []);
                      formik.setFieldValue('suppliers', val || []);
                    }}
                    value={
                      formik?.values?.suppliers?.label
                        ? {
                          label: formik.values.suppliers?.label,
                          value: formik.values.suppliers?.value,
                        }
                        : null
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          {(formik.values.product_type === 'composite' ||
            formik.values.product_type === 'recipe_based') && (
              <div className="card recipe-ingredients mb-md-3 mb-0">
                <div className="card-header p-3 d-flex align-items-center justify-content-between">
                  <h4 className="mb-0">Recipe / Ingredients</h4>
                  <button
                    type="button"
                    className="btn btn-primary btn-add-ingredient"
                    onClick={handleAddIngredient}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-0" />
                    <span>Add Ingredient</span>
                  </button>
                </div>

                <div className="recipe-ingredients-wrapper">
                  {ingredients.length > 0 ? (
                    <div className="ingredients-section mt-0">
                      {/* Table */}
                      <div className="ingredients-table-responsive">
                        <table className="table ingredients-table">
                          <thead>
                            <tr>
                              <th className="th-index text-center">#</th>
                              <th className="th-ingredient">
                                Ingredient{' '}
                                <span className="required-star">*</span>
                              </th>
                              <th className="th-qty text-center">
                                <span className="d-inline d-sm-none">Qty</span>
                                <span className="d-none d-sm-inline">
                                  Qty / Weight
                                </span>{' '}
                                <span className="required-star">*</span>
                              </th>
                              <th className="th-action text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ingredients.map((item, index) => {
                              const isSearching = activeSearchIndex === index;

                              return (
                                <tr
                                  key={item.id}
                                  style={
                                    isSearching
                                      ? { position: 'relative', zIndex: 1050 }
                                      : undefined
                                  }
                                >
                                  {/* # Index */}
                                  <td className="td-index text-center text-muted fw-medium">
                                    {index + 1}
                                  </td>

                                  {/* Ingredient (Raw Material) Column */}
                                  <td
                                    className="td-ingredient"
                                    style={
                                      isSearching
                                        ? { position: 'relative', zIndex: 1050 }
                                        : undefined
                                    }
                                  >
                                    <div
                                      className="recipe-cell-container"
                                      ref={(el) =>
                                        (searchContainerRefs.current[index] = el)
                                      }
                                    >
                                      {/* 1. When actively searching: Show Search Input & Dropdown ONLY for this row */}
                                      {isSearching ? (
                                        <div className="recipe-search-container">
                                          <div className="input-group">
                                            <span className="input-group-text">
                                              <FontAwesomeIcon
                                                icon={faMagnifyingGlass}
                                              />
                                            </span>
                                            <input
                                              type="text"
                                              className="form-control recipe-search-input"
                                              placeholder="Search product / raw material..."
                                              value={searchQuery}
                                              autoFocus
                                              autoComplete="off"
                                              onChange={(e) =>
                                                setSearchQuery(e.target.value)
                                              }
                                              onKeyDown={(e) => {
                                                if (e.key === 'Escape') {
                                                  setActiveSearchIndex(null);
                                                  setSearchQuery('');
                                                }
                                              }}
                                            />
                                          </div>

                                          {/* Dropdown Menu - strictly rendered ONLY for active row */}
                                          <div className="recipe-search-dropdown">
                                            {getFilteredOptions().length > 0 ? (
                                              getFilteredOptions().map((opt) => {
                                                const avatar = makeAvatar(
                                                  opt.name,
                                                );
                                                const isSelected =
                                                  item.sku === opt.sku;

                                                return (
                                                  <div
                                                    key={opt.sku || opt.value}
                                                    className={`recipe-search-item ${isSelected ? 'active' : ''
                                                      }`}
                                                    onMouseDown={(e) => {
                                                      e.preventDefault();
                                                      handleSelectProduct(
                                                        index,
                                                        opt,
                                                      );
                                                    }}
                                                  >
                                                    {/* Circle Avatar with Initials (SR / WF / etc.) */}
                                                    <div className="recipe-avatar">
                                                      {opt.image ? (
                                                        <img
                                                          src={opt.image}
                                                          alt={opt.name}
                                                          style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            borderRadius: '6px',
                                                          }}
                                                        />
                                                      ) : (
                                                        <span>{avatar}</span>
                                                      )}
                                                    </div>

                                                    {/* Name & Phone/SKU details */}
                                                    <div className="recipe-info">
                                                      <div className="recipe-name">
                                                        {opt.name}
                                                      </div>
                                                      <div className="recipe-sku">
                                                        {opt.sku}
                                                      </div>
                                                    </div>
                                                  </div>
                                                );
                                              })
                                            ) : (
                                              <div className="text-center py-3 text-muted small">
                                                No raw material found
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ) : item.name ? (
                                        /* 2. When NOT searching and product is selected: Show Selected Card */
                                        <div
                                          className="recipe-selected-card d-flex align-items-center justify-content-between"
                                          onClick={() => handleStartSearch(index)}
                                          title="Click to search and change ingredient"
                                        >
                                          <div className="d-flex align-items-center gap-2 overflow-hidden">
                                            {item.image ? (
                                              <img
                                                src={item.image}
                                                alt={item.name}
                                                style={{
                                                  width: '34px',
                                                  height: '34px',
                                                  objectFit: 'cover',
                                                  borderRadius: '6px',
                                                  backgroundColor: '#f8fafc',
                                                  flexShrink: 0,
                                                }}
                                              />
                                            ) : (
                                              <div
                                                className="d-flex align-items-center justify-content-center bg-light text-muted"
                                                style={{
                                                  width: '34px',
                                                  height: '34px',
                                                  borderRadius: '6px',
                                                  fontSize: '14px',
                                                  border: '1px solid #e2e8f0',
                                                  flexShrink: 0,
                                                }}
                                              >
                                                <FontAwesomeIcon icon={faBox} />
                                              </div>
                                            )}
                                            <div
                                              className="text-start overflow-hidden"
                                              style={{ lineHeight: 1.3 }}
                                            >
                                              <div className="fw-semibold text-dark small text-truncate">
                                                {item.name}
                                              </div>
                                              <div
                                                className="text-muted text-truncate"
                                                style={{ fontSize: '11px' }}
                                              >
                                                SKU: {item.sku || 'N/A'}
                                              </div>
                                            </div>
                                          </div>
                                          <div className="text-muted ps-2 pe-1">
                                            <FontAwesomeIcon
                                              icon={faChevronDown}
                                              style={{ fontSize: '12px' }}
                                            />
                                          </div>
                                        </div>
                                      ) : (
                                        /* 3. When NOT searching and NO product selected: Show clean placeholder card */
                                        <div
                                          className="recipe-selected-card d-flex align-items-center justify-content-between text-muted"
                                          onClick={() => handleStartSearch(index)}
                                          title="Click to select ingredient"
                                          style={{ cursor: 'pointer' }}
                                        >
                                          <div className="d-flex align-items-center gap-2">
                                            <FontAwesomeIcon
                                              icon={faMagnifyingGlass}
                                              className="text-secondary"
                                            />
                                            <span className="small text-secondary">
                                              Search product / raw material...
                                            </span>
                                          </div>
                                          <div className="text-muted ps-2 pe-1">
                                            <FontAwesomeIcon
                                              icon={faChevronDown}
                                              style={{ fontSize: '12px' }}
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </td>

                                  {/* Quantity */}
                                  <td className="td-qty text-center">
                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                      <input
                                        type="number"
                                        className="qty-input form-control text-center"
                                        value={item.quantity}
                                        min="0"
                                        step="any"
                                        onChange={(e) =>
                                          handleQuantityChange(
                                            index,
                                            e.target.value,
                                          )
                                        }
                                      />
                                      {/* {item.unit && (
                                        <span
                                          className="badge bg-light text-dark border px-2 py-1.5"
                                          style={{ fontSize: '11px' }}
                                        >
                                          {item.unit}
                                        </span>
                                      )} */}
                                    </div>
                                  </td>

                                  {/* Action */}
                                  <td className="td-action text-center">
                                    <Button
                                      type="button"
                                      onClick={() =>
                                        handleRemoveIngredient(index)
                                      }
                                      className="link text-danger p-0 btn-delete-row"
                                    >
                                      <i className="fa-solid fa-trash text-danger me-0"></i>
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div className="recipe-summary-box-wrp">
                        <div className="recipe-summary-box">
                          <div className="calculator-badge">
                            <FontAwesomeIcon icon={faCalculator} />
                          </div>
                          <div className="summary-cost-content">
                            <div className="summary-label">
                              <span>Total ingredient cost per unit</span>
                              <span
                                className="summary-info-icon"
                                title="Total cost calculated from all ingredients per unit"
                              >
                                <InfoOutlineIcon size={15} />
                              </span>
                            </div>
                            <div className="summary-amount">₹ {displayedCost}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted">
                      <p className="mb-2">No ingredients added yet.</p>
                      <button
                        type="button"
                        className="btn btn-primary btn-add-ingredient"
                        onClick={handleAddIngredient}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                        <span>Add Ingredient</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>
        <div className="col-12 d-flex justify-content-end gap-3 d-flex d-md-none md-mb-0 mb-3">
          <Button
            type="submit"
            name={isEditMode ? t('common.update') : t('common.submit')}
            className="primary"
            loading={loading.update}
          />
          <LinkButton
            path={routes.products}
            label={t('common.cancel')}
            className="link text-danger fw-medium text-decoration-underline"
          />
        </div>
      </form>
    </>
  );
};
export default CreateProduct;
